import { useEffect, useRef, useState } from "react";
import { RotateCcw, Grid3x3, ShieldAlert, ZoomIn, Move, SquareDashedMousePointer } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { api, mediaUrl, ASPECTS } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { FacePanel } from "@/components/editor/FacePanel";

const SAFE = {
  tiktok: { top: 12, bottom: 22, right: 14 }, instagram_reels: { top: 10, bottom: 24, right: 12 },
  youtube_shorts: { top: 9, bottom: 20, right: 13 }, facebook: { top: 10, bottom: 22, right: 12 }, snapchat: { top: 10, bottom: 18, right: 4 },
};
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
export const DEFAULT_CAM = [0.68, 0.02, 0.3, 0.3];
const FKEYS = ["fit", "crop_x", "crop_y", "zoom", "split_ratio", "cam_rect", "cam_zoom", "cam_x", "cam_y"];

export const fgBox = (sa, ta, fit, z) => {
  const s = z * (fit === "crop" ? Math.max(ta / sa, 1) : Math.min(ta / sa, 1));
  return { w: ((sa * s) / ta) * 100, h: s * 100 };
};

const PRESETS = [
  ["center", { crop_x: 0.5, crop_y: 0.5, zoom: 1 }],
  ["facecam", { fit: "blur", crop_x: 0.5, crop_y: 0.5, zoom: 1, _facecamTop: true }],
  ["gameplay", { fit: "crop", crop_x: 0.5, crop_y: 0.5, zoom: 1 }],
];

const usePanZoom = (ref, getSpan, onPan, onZoom) => {
  const ptrs = useRef(new Map());
  const pinch = useRef(null);
  const zoomRef = useRef(onZoom);
  zoomRef.current = onZoom;
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onWheel = (e) => { e.preventDefault(); zoomRef.current(Math.exp(-e.deltaY * 0.0015)); };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [ref]);
  const down = (e) => { e.currentTarget.setPointerCapture(e.pointerId); ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY }); };
  const up = (e) => { ptrs.current.delete(e.pointerId); pinch.current = null; };
  const move = (e) => {
    const prev = ptrs.current.get(e.pointerId);
    if (!prev) return;
    ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (ptrs.current.size === 2) {
      const [a, b] = [...ptrs.current.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinch.current) onZoom(d / pinch.current);
      pinch.current = d;
      return;
    }
    const [sx, sy] = getSpan();
    onPan(Math.abs(sx) > 1 ? (e.clientX - prev.x) / sx : 0, Math.abs(sy) > 1 ? (e.clientY - prev.y) / sy : 0);
  };
  return { onPointerDown: down, onPointerMove: move, onPointerUp: up, onPointerCancel: up };
};

const SyncVideo = ({ video, current, style, testid }) => {
  const vref = useRef(null);
  useEffect(() => { const v = vref.current; if (v && Math.abs(v.currentTime - current) > 0.25) v.currentTime = current; }, [current]);
  return <video ref={vref} src={mediaUrl(`/videos/${video.id}/stream`)} muted playsInline preload="auto" className="absolute max-w-none pointer-events-none" style={style} data-testid={testid} />;
};

const Zone = ({ video, current, sa, za, rect, z, cx, cy, onChange, testid, style, children }) => {
  const box = useRef(null);
  const [rx, ry, rw, rh] = rect;
  const RW = rw * sa, RH = rh;
  const s = z * Math.max(za / RW, 1 / RH);
  const dispW = (RW * s) / za, dispH = RH * s;
  const ox = (1 - dispW) * cx, oy = (1 - dispH) * cy;
  const vid = { width: `${((sa * s) / za) * 100}%`, height: `${s * 100}%`, left: `${(ox - (rx * sa * s) / za) * 100}%`, top: `${(oy - ry * s) * 100}%` };
  const handlers = usePanZoom(box, () => { const r = box.current.getBoundingClientRect(); return [r.width * (1 - dispW), r.height * (1 - dispH)]; },
    (dx, dy) => onChange((p) => ({ x: clamp(p.x + dx, 0, 1), y: clamp(p.y + dy, 0, 1) })),
    (k) => onChange((p) => ({ z: clamp(p.z * k, 1, 3) })));
  return (
    <div ref={box} {...handlers} data-testid={testid} className="relative overflow-hidden cursor-grab active:cursor-grabbing touch-none select-none" style={style}>
      <SyncVideo video={video} current={current} style={vid} testid={`${testid}-video`} />
      {children}
    </div>
  );
};

const Overlays = ({ cfg, ta, showGrid, showSafe, t, light = false }) => {
  const safe = SAFE[cfg.platform] && ta < 1 ? SAFE[cfg.platform] : null;
  return (<>
    {showGrid && (<div className="absolute inset-0 pointer-events-none" data-testid="framing-grid">
      {[33.33, 66.66].map((p) => <div key={`v${p}`} className="absolute top-0 bottom-0 w-px bg-white/35" style={{ left: `${p}%` }} />)}
      {[33.33, 66.66].map((p) => <div key={`h${p}`} className="absolute left-0 right-0 h-px bg-white/35" style={{ top: `${p}%` }} />)}
    </div>)}
    {showSafe && safe && (<div className={`absolute inset-0 pointer-events-none text-[8px] font-mono uppercase text-rose-200 ${light ? "opacity-45 [&_*]:text-transparent" : ""}`} data-testid="framing-safe-zones">
      <div className="absolute inset-x-0 top-0 bg-rose-500/25 border-b border-dashed border-rose-300/70 px-1" style={{ height: `${safe.top}%` }}>{t("fr.safe_ui")}</div>
      <div className="absolute inset-x-0 bottom-0 bg-rose-500/25 border-t border-dashed border-rose-300/70 px-1 flex items-end" style={{ height: `${safe.bottom}%` }}>{t("fr.safe_caption")}</div>
      <div className="absolute right-0 bg-rose-500/20 border-l border-dashed border-rose-300/70" style={{ top: `${safe.top}%`, bottom: `${safe.bottom}%`, width: `${safe.right}%` }} />
    </div>)}
  </>);
};

const Stage = ({ video, cfg, setCfg, current, showGrid, showSafe }) => {
  const { t } = useI18n();
  const box = useRef(null);
  const sa = video.width && video.height ? video.width / video.height : 16 / 9;
  const ta = ASPECTS.find((a) => a.id === cfg.aspect)?.ratio || 9 / 16;
  const z = cfg.zoom || 1;
  const fg = fgBox(sa, ta, cfg.fit, z);
  const handlers = usePanZoom(box, () => { const r = box.current.getBoundingClientRect(); return [r.width * (1 - fg.w / 100), r.height * (1 - fg.h / 100)]; },
    (dx, dy) => setCfg((c) => ({ ...c, crop_x: clamp(c.crop_x + dx, 0, 1), crop_y: clamp((c.crop_y ?? 0.5) + dy, 0, 1) })),
    (k) => setCfg((c) => ({ ...c, zoom: clamp((c.zoom || 1) * k, 1, 3) })));
  const width = ta < 1 ? "min(100%, 220px)" : "100%";
  if (cfg.fit === "split") {
    const r = cfg.split_ratio ?? 0.35;
    const g = (k) => ({ x: cfg[`${k}_x`], y: cfg[`${k}_y`], z: cfg[k === "crop" ? "zoom" : "cam_zoom"] || 1 });
    const upd = (k) => (fn) => setCfg((c) => {
      const cur = { x: c[`${k}_x`] ?? 0.5, y: c[`${k}_y`] ?? 0.5, z: c[k === "crop" ? "zoom" : "cam_zoom"] || 1 };
      const n = { ...cur, ...fn(cur) };
      return { ...c, [`${k}_x`]: n.x, [`${k}_y`]: n.y, [k === "crop" ? "zoom" : "cam_zoom"]: n.z };
    });
    return (
      <div className="relative mx-auto overflow-hidden rounded-xl border border-white/10 bg-black flex flex-col" style={{ aspectRatio: ta, width }} data-testid="framing-stage">
        <Zone video={video} current={current} sa={sa} za={ta / r} rect={cfg.cam_rect || DEFAULT_CAM} z={g("cam").z} cx={g("cam").x ?? 0.5} cy={g("cam").y ?? 0.5}
          onChange={upd("cam")} testid="framing-zone-cam" style={{ height: `${r * 100}%` }}>
          <span className="absolute bottom-1.5 left-1.5 z-20 rounded bg-black/80 px-1.5 py-0.5 text-[9px] font-mono text-[#3ad7ff] pointer-events-none">{t("fr.zone_cam")} {Math.round(g("cam").z * 100)}%</span>
        </Zone>
        <div className="h-[3px] shrink-0 bg-gradient-to-r from-[#3ad7ff] via-[#2f7bff] to-[#a746ff] shadow-[0_0_12px_rgba(122,92,255,0.9)] z-10" data-testid="framing-split-line" />
        <Zone video={video} current={current} sa={sa} za={ta / (1 - r)} rect={[0, 0, 1, 1]} z={g("crop").z} cx={g("crop").x} cy={g("crop").y ?? 0.5}
          onChange={upd("crop")} testid="framing-zone-game" style={{ flex: 1 }}>
          <span className="absolute bottom-1 left-1 rounded bg-black/70 px-1.5 py-0.5 text-[9px] font-mono text-[#3ad7ff] pointer-events-none">{t("fr.zone_game")} {Math.round(g("crop").z * 100)}%</span>
        </Zone>
        <Overlays cfg={cfg} ta={ta} showGrid={showGrid} showSafe={showSafe} t={t} light />
      </div>
    );
  }
  const left = (100 - fg.w) * cfg.crop_x, top = (100 - fg.h) * (cfg.crop_y ?? 0.5);
  return (
    <div ref={box} {...handlers} data-testid="framing-stage"
      className="relative mx-auto overflow-hidden rounded-xl border border-white/10 bg-black cursor-grab active:cursor-grabbing touch-none select-none" style={{ aspectRatio: ta, width }}>
      {cfg.fit === "blur" && <img src={mediaUrl(`/videos/${video.id}/thumbnail`)} alt="" className="absolute inset-0 w-full h-full object-cover blur-xl brightness-75 scale-110" draggable={false} />}
      <SyncVideo video={video} current={current} testid="framing-video" style={{ width: `${fg.w}%`, height: `${fg.h}%`, left: `${left}%`, top: `${top}%` }} />
      <Overlays cfg={cfg} ta={ta} showGrid={showGrid} showSafe={showSafe} t={t} />
      <span className="absolute top-1.5 left-1.5 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-mono text-[#3ad7ff] pointer-events-none" data-testid="framing-zoom-label">{Math.round(z * 100)}%</span>
    </div>
  );
};

const CamPicker = ({ video, cfg, setCfg, current }) => {
  const { t } = useI18n();
  const box = useRef(null);
  const drag = useRef(null);
  const sa = video.width && video.height ? video.width / video.height : 16 / 9;
  const [rx, ry, rw, rh] = cfg.cam_rect || DEFAULT_CAM;
  const pt = (e) => { const r = box.current.getBoundingClientRect(); return [clamp((e.clientX - r.left) / r.width, 0, 1), clamp((e.clientY - r.top) / r.height, 0, 1)]; };
  const down = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    const [x, y] = pt(e);
    const inside = x >= rx && x <= rx + rw && y >= ry && y <= ry + rh;
    drag.current = inside ? { mode: "move", x, y, rect: [rx, ry, rw, rh] } : { mode: "draw", x, y };
  };
  const move = (e) => {
    const d = drag.current;
    if (!d) return;
    const [x, y] = pt(e);
    let rect;
    if (d.mode === "move") {
      const [ox, oy, w, h] = d.rect;
      rect = [clamp(ox + x - d.x, 0, 1 - w), clamp(oy + y - d.y, 0, 1 - h), w, h];
    } else {
      const w = Math.max(0.05, Math.abs(x - d.x)), h = Math.max(0.05, Math.abs(y - d.y));
      rect = [clamp(Math.min(x, d.x), 0, 1 - w), clamp(Math.min(y, d.y), 0, 1 - h), w, h];
    }
    setCfg((c) => ({ ...c, cam_rect: rect.map((v) => Math.round(v * 1000) / 1000) }));
  };
  return (
    <div className="space-y-1.5" data-testid="framing-cam-picker">
      <div className="text-[11px] text-slate-500 flex items-center gap-1.5"><SquareDashedMousePointer className="w-3.5 h-3.5" />{t("fr.cam_pick")}</div>
      <div ref={box} onPointerDown={down} onPointerMove={move} onPointerUp={() => { drag.current = null; }} data-testid="framing-cam-source"
        className="relative overflow-hidden rounded-lg border border-white/10 bg-black cursor-crosshair touch-none select-none" style={{ aspectRatio: sa }}>
        <SyncVideo video={video} current={current} style={{ inset: 0, width: "100%", height: "100%" }} testid="framing-cam-source-video" />
        <div className="absolute border-2 border-[#3ad7ff] bg-[#3ad7ff]/10 shadow-[0_0_0_9999px_rgba(4,5,10,0.55)] cursor-move" data-testid="framing-cam-rect"
          style={{ left: `${rx * 100}%`, top: `${ry * 100}%`, width: `${rw * 100}%`, height: `${rh * 100}%` }} />
      </div>
      <p className="text-[10px] text-slate-500">{t("fr.cam_hint")}</p>
    </div>
  );
};

const useFramingMemory = (cfg, setCfg) => {
  const prefs = useRef(null);
  const applied = useRef(null);
  const apply = (a) => {
    applied.current = a;
    const p = prefs.current?.[a];
    if (p) setCfg((c) => {
      const n = { ...c, ...p, fit: p.fit === "split" && a !== "9:16" ? "blur" : p.fit };
      return c.auto_face && c.fit === "split" && a === "9:16" ? { ...n, fit: "split", cam_rect: c.cam_rect } : n;
    });
    else if (a !== "9:16") setCfg((c) => (c.fit === "split" ? { ...c, fit: "blur" } : c));
  };
  useEffect(() => { api.get("/me/framing").then((r) => { prefs.current = r.data || {}; apply(cfg.aspect); }).catch(() => { prefs.current = {}; applied.current = cfg.aspect; }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!prefs.current) return;
    if (applied.current !== cfg.aspect) { apply(cfg.aspect); return; }
    if (cfg.fit === "split" && cfg.aspect !== "9:16") { setCfg((c) => ({ ...c, fit: "blur" })); return; }
  }, [cfg.aspect, cfg.fit]); // eslint-disable-line react-hooks/exhaustive-deps
  const snap = JSON.stringify(FKEYS.map((k) => cfg[k]));
  const pending = useRef(null);
  const flush = () => {
    const body = pending.current;
    pending.current = null;
    if (body) api.put("/me/framing", body).then((r) => { prefs.current = { ...prefs.current, [body.aspect]: r.data }; }).catch(() => {});
  };
  useEffect(() => {
    if (!prefs.current || applied.current !== cfg.aspect) return undefined;
    const p = Object.fromEntries(FKEYS.filter((k) => cfg[k] !== undefined && cfg[k] !== null).map((k) => [k, cfg[k]]));
    if (JSON.stringify(prefs.current[cfg.aspect] || {}) === JSON.stringify({ ...(prefs.current[cfg.aspect] || {}), ...p })) return undefined;
    pending.current = { aspect: cfg.aspect, prefs: p };
    const id = setTimeout(flush, 600);
    return () => clearTimeout(id);
  }, [snap, cfg.aspect]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => flush(), []); // eslint-disable-line react-hooks/exhaustive-deps
};

export const FramingPanel = ({ video, cfg, setCfg, current, onFacecam }) => {
  const { t } = useI18n();
  const [showGrid, setGrid] = useState(true);
  const [showSafe, setSafe] = useState(true);
  useFramingMemory(cfg, setCfg);
  const split = cfg.fit === "split";
  const toggle = (on, set, Icon, label, testid) => (
    <button type="button" onClick={() => set(!on)} data-testid={testid}
      className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] border transition-colors ${on ? "border-[#3ad7ff]/60 text-white bg-[#2f7bff]/15" : "border-white/10 text-slate-400"}`}><Icon className="w-3.5 h-3.5" />{label}</button>
  );
  const reset = () => setCfg((c) => ({ ...c, crop_x: 0.5, crop_y: 0.5, zoom: 1, cam_x: 0.5, cam_y: 0.5, cam_zoom: 1, ...(split ? { split_ratio: 0.35 } : {}) }));
  const preset = (p) => setCfg((c) => {
    const { _facecamTop, ...rest } = p;
    return _facecamTop && c.aspect === "9:16" ? { ...c, fit: "split", split_ratio: c.split_ratio ?? 0.35, cam_rect: c.cam_rect || DEFAULT_CAM, cam_zoom: 1, cam_x: 0.5, cam_y: 0.5, crop_x: 0.5, crop_y: 0.5, zoom: 1 }
      : { ...c, ...rest, ...(_facecamTop ? { crop_y: 0 } : {}) };
  });
  return (
    <div className="space-y-3" data-testid="framing-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] text-slate-500 flex items-center gap-1.5"><Move className="w-3.5 h-3.5" />{t("fr.title")}</div>
        <button type="button" onClick={reset} data-testid="framing-reset-btn" className="cx-metal rounded-full px-3 py-1 text-[11px] flex items-center gap-1.5"><RotateCcw className="w-3 h-3" />{t("fr.reset")}</button>
      </div>
      <FacePanel video={video} cfg={cfg} setCfg={setCfg} onFacecam={onFacecam} />
      {split && <CamPicker video={video} cfg={cfg} setCfg={setCfg} current={current} />}
      <Stage video={video} cfg={cfg} setCfg={setCfg} current={current} showGrid={showGrid} showSafe={showSafe} />
      <p className="text-[10px] text-slate-500 text-center">{t(split ? "fr.hint_split" : "fr.hint")}</p>
      {split ? (
        <div className="flex items-center gap-3" data-testid="framing-split-control">
          <span className="text-[11px] text-slate-400 shrink-0">{t("fr.split_ratio")}</span>
          <Slider value={[(cfg.split_ratio ?? 0.35) * 100]} min={25} max={50} step={1} onValueChange={([v]) => setCfg((c) => ({ ...c, split_ratio: v / 100 }))} />
          <span className="text-[11px] font-mono text-white w-11 text-right" data-testid="framing-split-value">{Math.round((cfg.split_ratio ?? 0.35) * 100)}%</span>
        </div>
      ) : (
        <div className="flex items-center gap-3" data-testid="framing-zoom-control">
          <ZoomIn className="w-4 h-4 text-slate-400 shrink-0" />
          <Slider value={[(cfg.zoom || 1) * 100]} min={100} max={300} step={1} onValueChange={([v]) => setCfg((c) => ({ ...c, zoom: v / 100 }))} />
          <span className="text-[11px] font-mono text-white w-11 text-right">{Math.round((cfg.zoom || 1) * 100)}%</span>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {PRESETS.map(([k, p]) => (
          <button key={k} type="button" onClick={() => preset(p)} data-testid={`framing-preset-${k}`} className="cx-metal rounded-lg px-3 py-1.5 text-[11px]">{t(`fr.preset_${k}`)}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {toggle(showGrid, setGrid, Grid3x3, t("fr.grid"), "framing-grid-toggle")}
        {toggle(showSafe, setSafe, ShieldAlert, t("fr.safe"), "framing-safe-toggle")}
      </div>
      <p className="text-[10px] text-slate-500" data-testid="framing-memory-note">{t("fr.memory")}</p>
    </div>
  );
};
